/**
 * Security settings page. Lets a signed-in moderator manage their
 * registered WebAuthn passkeys: see what is registered, add a new
 * passkey, or remove one.
 *
 * Passkeys are opt-in; TOTP MFA remains the universal backstop for
 * sign-in. A moderator with zero passkeys keeps using email/password
 * + TOTP exactly as before. A moderator with one or more passkeys
 * also sees a passkey option on the sign-in screen.
 */

import { useCallback, useEffect, useState } from 'react';
import { announce } from '../lib/announce';
import {
  deletePasskey,
  isPasskeySupported,
  listPasskeys,
  registerPasskey,
  type PasskeySummary,
} from '../lib/webauthn';

interface SecuritySettingsProps {
  /**
   * Called when the moderator hits the back-link, to navigate them
   * back to the dashboard. Provided by App.tsx via the hash router.
   */
  onBack: () => void;
}

type Status =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'registering' }
  | { kind: 'deleting'; credentialId: string }
  | { kind: 'error'; message: string };

function formatTransports(transports: string[]): string {
  if (transports.length === 0) return 'this device';
  return transports
    .map((t) => {
      if (t === 'internal') return 'this device';
      if (t === 'hybrid') return 'phone / cross-device';
      if (t === 'usb') return 'USB security key';
      if (t === 'nfc') return 'NFC security key';
      if (t === 'ble') return 'Bluetooth security key';
      return t;
    })
    .join(', ');
}

function formatCreated(d?: Date): string {
  if (!d) return '';
  try {
    return d.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return '';
  }
}

export default function SecuritySettings({ onBack }: SecuritySettingsProps) {
  const [passkeys, setPasskeys] = useState<PasskeySummary[] | null>(null);
  const [status, setStatus] = useState<Status>({ kind: 'idle' });
  const supported = isPasskeySupported();

  const reload = useCallback(async () => {
    setStatus({ kind: 'loading' });
    try {
      const list = await listPasskeys();
      setPasskeys(list);
      setStatus({ kind: 'idle' });
    } catch (e) {
      const err = e as Error;
      setStatus({
        kind: 'error',
        message: err?.message || 'Could not load your passkeys.',
      });
    }
  }, []);

  useEffect(() => {
    if (supported) {
      void reload();
    } else {
      setPasskeys([]);
    }
  }, [reload, supported]);

  const onRegister = async () => {
    setStatus({ kind: 'registering' });
    try {
      await registerPasskey();
      announce('Passkey added.');
      await reload();
    } catch (e) {
      const err = e as Error;
      // NotAllowedError fires when the user cancels the OS picker.
      // Treat as a soft outcome, not an error banner.
      if (err?.name === 'NotAllowedError') {
        setStatus({ kind: 'idle' });
        return;
      }
      setStatus({
        kind: 'error',
        message:
          err?.message ||
          'Could not register that passkey. Check that your browser supports it and try again.',
      });
    }
  };

  const onDelete = async (credentialId: string, name: string) => {
    const confirmed = window.confirm(
      `Remove the passkey "${name}"? You can register a new one at any time, and you will keep your email/password sign-in.`,
    );
    if (!confirmed) return;
    setStatus({ kind: 'deleting', credentialId });
    try {
      await deletePasskey(credentialId);
      announce('Passkey removed.');
      await reload();
    } catch (e) {
      const err = e as Error;
      setStatus({
        kind: 'error',
        message: err?.message || 'Could not remove that passkey. Try again.',
      });
    }
  };

  return (
    <section
      className="security-settings"
      aria-labelledby="security-settings-heading"
    >
      <div className="security-settings__header">
        <button
          type="button"
          className="security-settings__back"
          onClick={onBack}
        >
          &larr; Back
        </button>
        <h1 id="security-settings-heading">Security</h1>
      </div>

      <div className="security-settings__intro">
        <h2>Passkeys</h2>
        <p>
          Sign in with Face ID, Touch ID, Windows Hello, or a security key.
          Passkeys are optional. Your password and authenticator app keep
          working either way.
        </p>
      </div>

      {!supported && (
        <p className="security-settings__notice">
          This browser cannot register a passkey. Try a recent Chrome,
          Safari, Edge, or Firefox build.
        </p>
      )}

      {status.kind === 'error' && (
        <p className="security-settings__error" role="alert">
          {status.message}
        </p>
      )}

      {supported && (
        <div className="security-settings__actions">
          <button
            type="button"
            className="btn btn-primary"
            onClick={onRegister}
            disabled={status.kind === 'registering' || status.kind === 'loading'}
          >
            {status.kind === 'registering'
              ? 'Waiting for your device...'
              : 'Add a passkey'}
          </button>
        </div>
      )}

      <div className="security-settings__list" aria-live="polite">
        {status.kind === 'loading' && passkeys === null && (
          <p>Loading your passkeys...</p>
        )}
        {passkeys && passkeys.length === 0 && supported && (
          <p className="security-settings__empty">
            You have not registered any passkeys yet.
          </p>
        )}
        {passkeys && passkeys.length > 0 && (
          <ul className="security-settings__items">
            {passkeys.map((p) => {
              const isDeleting =
                status.kind === 'deleting' && status.credentialId === p.credentialId;
              return (
                <li key={p.credentialId} className="security-settings__item">
                  <div className="security-settings__item-main">
                    <p className="security-settings__item-name">{p.name}</p>
                    <p className="security-settings__item-meta">
                      {formatTransports(p.transports)}
                      {p.createdAt ? ` · Added ${formatCreated(p.createdAt)}` : ''}
                    </p>
                  </div>
                  <button
                    type="button"
                    className="btn btn-secondary security-settings__remove"
                    onClick={() => onDelete(p.credentialId, p.name)}
                    disabled={isDeleting}
                  >
                    {isDeleting ? 'Removing...' : 'Remove'}
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </section>
  );
}
